// @ts-check

/**
 * @file Renders the timelogs index route.
 */

import {FlashNotifications} from "flash-notifications"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useRouter} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, View} from "react-native-propforge"
import DateRangePicker from "../../src/components/date-range-picker"
import Link from "../../src/components/link"
import TextInput from "../../src/components/text-input"
import buildRoutePath from "../../src/shared/build-route-path"
import Button from "../../src/components/button"
import confirmModal from "../../src/shared/confirm-modal"
import EmptyState from "../../src/components/empty-state"
import errorMessage from "../../src/shared/error-message"
import LoadingState from "../../src/components/loading-state"
import PageHeader from "../../src/components/page-header"
import Pagination from "../../src/components/pagination"
import ProjectsSelect from "../../src/components/projects/select"
import {Timelog} from "../../src/frontend-models/timelog"
import UsersSelect from "../../src/components/users/select"
import {performanceMeasure, performanceNow} from "../../src/shared/performance-debug"
import {identifierListParam, optionalRouteParam} from "../../src/shared/route-params"
import {pathForQueryParamUpdates} from "on-location-changed/build/query-param-updates"
import useCurrentQueryParams from "on-location-changed/build/use-current-query-params"
import useSelectedQueryParams from "on-location-changed/build/use-selected-query-params"
import useThemeColors, {useThemeName} from "../../src/shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"
import ScreenTitle from "../../src/components/screen-title"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {Record<string, string | string[] | undefined>} RouteQueryParams */
/** @type {Record<string, object>} */
const styles = {}
const PAGE_SIZE = 30
const TIMELOG_INDEX_PRELOAD_SELECT = {
  Task: ["id", "name", "projectId"],
  User: ["firstName", "id", "lastName"]
}
const TIMELOG_INDEX_SELECT = ["comment", "date", "id", "invoiced", "taskId", "timeSeconds", "transportLength", "userId"]
const TIMELOGS_QUERY_PARAM_SELECTOR = ["date_from", "date_to", "invoiced", "project_ids", "search", "user_ids"]

/**
 * Formats a duration in seconds as compact hours and minutes.
 * @param {number | null | undefined} seconds Duration in seconds.
 * @returns {string} Compact duration label.
 */
function formatTime(seconds) {
  if (!seconds) return ""
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h`
  return `${minutes}m`
}

/**
 * Formats a date-like value with the runtime locale.
 * @param {Date | string | null | undefined} value Date value to format.
 * @returns {string} Localized date label.
 */
function formatDate(value) {
  if (!value) return ""
  if (value instanceof Date) return value.toLocaleDateString()
  const dateString = String(value)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return new Date(`${dateString}T00:00:00`).toLocaleDateString()
  }
  return new Date(dateString).toLocaleDateString()
}

/**
 * Builds the first day of the current local month.
 * @returns {string} Date string in YYYY-MM-DD format.
 */
function firstOfMonth() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`
}

/**
 * Builds the current local date.
 * @returns {string} Date string in YYYY-MM-DD format.
 */
function today() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
}

/** @typedef {"any" | "invoiced" | "not_invoiced"} InvoicedFilter */

/** @typedef {{[key: string]: unknown}} TimelogsIndexScreenProps */

/**
 * @typedef {object} PendingTimelogsIndexFilterTransition
 * @property {string} sourceFiltersKey Filter key before the route update.
 * @property {string} routeFiltersKey Filter key expected after the route update.
 * @property {number} summaryCount Visible summary row count before the filter update.
 * @property {number} summaryInvoicedCount Visible summary invoiced row count before the filter update.
 * @property {boolean} summaryLoaded Whether the summary was visible before the filter update.
 * @property {number} summaryNotInvoicedCount Visible summary uninvoiced row count before the filter update.
 * @property {number} summaryTotalSeconds Visible summary tracked seconds before the filter update.
 * @property {number} summaryTotalTransportSeconds Visible summary travel seconds before the filter update.
 * @property {Timelog[]} timelogs Visible rows before the filter update.
 * @property {number} totalCount Visible row count before the filter update.
 */

/** @type {PendingTimelogsIndexFilterTransition | null} */
let pendingFilterTransitionState = null

/**
 * @typedef {object} TimelogsIndexFilters
 * @property {string} dateFrom Start date filter.
 * @property {string} dateTo End date filter.
 * @property {InvoicedFilter} invoicedFilter Active invoicing filter.
 * @property {string[]} projectIds Selected project identifiers.
 * @property {string} searchQuery Search text filter.
 * @property {string[]} userIds Selected user identifiers.
 */

/**
 * @typedef {object} TimelogsIndexScreenState
 * @property {string} dateFrom Start date filter.
 * @property {string} dateTo End date filter.
 * @property {boolean} exporting Whether CSV export is running.
 * @property {InvoicedFilter} invoicedFilter Active invoicing filter.
 * @property {boolean} loaded Whether the initial timelog load has completed.
 * @property {boolean} loading Whether timelog rows are loading.
 * @property {boolean} marking Whether bulk invoicing is running.
 * @property {number} page Current result page.
 * @property {string[]} projectIds Selected project identifiers.
 * @property {string} searchQuery Search text filter.
 * @property {{kind: "error" | "success", text: string} | null} status Status message shown above results.
 * @property {number} summaryCount Summary row count.
 * @property {number} summaryInvoicedCount Summary invoiced row count.
 * @property {boolean} summaryLoaded Whether aggregate summary data has loaded.
 * @property {number} summaryNotInvoicedCount Summary uninvoiced row count.
 * @property {number} summaryTotalSeconds Summary tracked seconds.
 * @property {number} summaryTotalTransportSeconds Summary travel seconds.
 * @property {Timelog[]} timelogs Loaded timelog rows.
 * @property {number} totalCount Number of matching rows.
 * @property {string[]} userIds Selected user identifiers.
 */

/**
 * Renders filters, bulk actions, summaries, and paginated timelog rows.
 * @augments {ShapeComponent<TimelogsIndexScreenProps, TimelogsIndexScreenState>}
 */
class TimelogsIndexScreen extends ShapeComponent {
  /** @type {() => RouteQueryParams} */
  currentQueryParams
  /** @type {RouteQueryParams} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {string} */
  routeFiltersKey = ""
  loadRequestId = 0
  searchDebounceTimer = /** @type {ReturnType<typeof setTimeout> | null} */ null
  /** @type {TimelogsIndexScreenState} */
  state = {
    dateFrom: "",
    dateTo: "",
    exporting: false,
    invoicedFilter: /** @type {InvoicedFilter} */ ("any"),
    loaded: false,
    loading: true,
    marking: false,
    page: 1,
    projectIds: /** @type {string[]} */ [],
    searchQuery: "",
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null,
    summaryCount: 0,
    summaryInvoicedCount: 0,
    summaryLoaded: false,
    summaryNotInvoicedCount: 0,
    summaryTotalSeconds: 0,
    summaryTotalTransportSeconds: 0,
    timelogs: /** @type {Timelog[]} */ [],
    totalCount: 0,
    userIds: /** @type {string[]} */ []
  }

  /**
   * Initializes route-derived filters and loading effects.
   * @returns {void} Nothing.
   */
  setup() {
    useLocale()
    const params = /** @type {RouteQueryParams} */ (useSelectedQueryParams(TIMELOGS_QUERY_PARAM_SELECTOR))
    const currentQueryParams = /** @type {() => RouteQueryParams} */ (useCurrentQueryParams())
    const router = useRouter()
    const routeFilters = filtersFromParams(params)
    const routeFiltersKey = serializeFilters(routeFilters)
    this.colors = useThemeColors()
    this.themeName = useThemeName()

    this.currentQueryParams = currentQueryParams
    this.params = params
    this.router = router
    if (this.routeFiltersKey !== routeFiltersKey) {
      const resetPage = this.routeFiltersKey !== ""
      this.routeFiltersKey = routeFiltersKey
      this.state.dateFrom = routeFilters.dateFrom
      this.state.dateTo = routeFilters.dateTo
      this.state.invoicedFilter = routeFilters.invoicedFilter
      this.state.projectIds = routeFilters.projectIds
      this.state.searchQuery = routeFilters.searchQuery
      this.state.userIds = routeFilters.userIds
      if (resetPage) this.state.page = 1
    }
    this.consumePendingFilterTransitionState(routeFiltersKey)
    const page = this.s.page
    const searchQuery = this.s.searchQuery
    const dateFrom = this.s.dateFrom
    const dateTo = this.s.dateTo
    const userIdsKey = this.s.userIds.join(",")
    const projectIdsKey = this.s.projectIds.join(",")
    const invoicedFilter = this.s.invoicedFilter
    useEffect(() => {
      void this.loadTimelogs()
      void this.loadSummary()
    }, [
      page,
      searchQuery,
      dateFrom,
      dateTo,
      userIdsKey,
      projectIdsKey,
      invoicedFilter
    ])
    useEffect(() => {
      return () => {
        if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      }
    }, [])
  }

  /**
   * Renders the timelogs index route.
   * @returns {ReactNode} Timelogs index content.
   */
  render() {
    const {exporting, marking, status} = this.s
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="timelogsIndexScreen"
      >
        <ScreenTitle>{_("Timelogs")}</ScreenTitle>
        <View
          testID="timelogsIndexScreen/content"
        >
          <PageHeader
            actions={
              <View
                style={styles.headerActionsInner ||= {
                  alignItems: "center",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 10
                }}
                testID="timelogsIndexScreen/headerActionsInner"
              >
                <Button
                  disabled={exporting}
                  iconName="file-csv"
                  label={exporting ? _("Exporting...") : _("Export CSV")}
                  onPress={this.tt.onExportCsvPress}
                  testID="timelogsIndexScreen/exportButton"
                  variant="secondary"
                />
                <Button
                  disabled={marking}
                  iconName="circle-check"
                  label={marking ? _("Marking...") : _("Mark all invoiced")}
                  onPress={this.tt.onMarkInvoicedPress}
                  testID="timelogsIndexScreen/markInvoicedButton"
                />
                <Button
                  href={buildRoutePath(this.tt.params, "/timelogs/new")}
                  iconName="plus"
                  label={_("Add timelog")}
                  testID="timelogsIndexScreen/newButton"
                  variant="neutral"
                />
              </View>
            }
            subtitle={_("Filter, export and bulk-invoice tracked time")}
            testID="timelogsIndexScreen/header"
            title={_("Timelogs")}
          />
          {this.s.summaryLoaded && this.renderSummary()}
          {this.renderFilters()}
          {status &&
            <Text
              style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                color: status.kind === "error" ? this.tt.colors.dangerText : this.tt.colors.successText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="timelogsIndexScreen/status"
            >
              {status.text}
            </Text>
          }
          {this.renderTimelogsResults()}
          {this.renderPagination()}
        </View>
      </View>
    )
  }

  /**
   * Renders pagination when rows are loaded.
   * @returns {ReactNode} Pagination controls, or null while loading.
   */
  renderPagination() {
    const {loaded, page, totalCount} = this.s
    if (!loaded) return null
    return (
      <Pagination
        onPageChange={this.tt.onPageChange}
        page={page}
        pageSize={PAGE_SIZE}
        testID="timelogsIndexScreen/pagination"
        totalCount={totalCount}
      />
    )
  }

  /**
   * Renders the aggregate summary card.
   * @returns {ReactNode} Summary card.
   */
  renderSummary() {
    const {
      summaryCount,
      summaryInvoicedCount,
      summaryNotInvoicedCount,
      summaryTotalSeconds,
      summaryTotalTransportSeconds
    } = this.s
    return (
      <View
        style={styles[`summaryCard-${this.tt.themeName}`] ||= {
          backgroundColor: this.tt.colors.surface,
          borderColor: this.tt.colors.border,
          borderRadius: 12,
          borderWidth: 1,
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 24,
          marginBottom: 16,
          padding: 16
        }}
        testID="timelogsIndexScreen/summary"
      >
        <View
          style={styles.summaryItem ||= {
            gap: 4,
            minWidth: 120
          }}
          testID="timelogsIndexScreen/summary/hours"
        >
          <Text
            style={styles[`summaryLabel-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              fontSize: 12,
              fontWeight: 600
            }}
            testID="timelogsIndexScreen/summary/hours/label"
          >
            {_("Total hours")}
          </Text>
          <Text
            style={styles[`summaryValue-${this.tt.themeName}`] ||= {
              fontSize: 20,
              fontWeight: 700
            }}
            testID="timelogsIndexScreen/summary/hours/value"
          >
            {(summaryTotalSeconds / 3600).toFixed(1)}
          </Text>
        </View>
        <View
          style={styles.summaryItemTransport ||= {
            gap: 4,
            minWidth: 120
          }}
          testID="timelogsIndexScreen/summary/transport"
        >
          <Text
            style={styles[`summaryLabelTransport-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              fontSize: 12,
              fontWeight: 600
            }}
            testID="timelogsIndexScreen/summary/transport/label"
          >
            {_("Transport hours")}
          </Text>
          <Text
            style={styles[`summaryValueTransport-${this.tt.themeName}`] ||= {
              fontSize: 20,
              fontWeight: 700
            }}
            testID="timelogsIndexScreen/summary/transport/value"
          >
            {(summaryTotalTransportSeconds / 3600).toFixed(1)}
          </Text>
        </View>
        <View
          style={styles.summaryItemEntries ||= {
            gap: 4,
            minWidth: 120
          }}
          testID="timelogsIndexScreen/summary/entries"
        >
          <Text
            style={styles[`summaryLabelEntries-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              fontSize: 12,
              fontWeight: 600
            }}
            testID="timelogsIndexScreen/summary/entries/label"
          >
            {_("Entries")}
          </Text>
          <Text
            style={styles[`summaryValueEntries-${this.tt.themeName}`] ||= {
              fontSize: 20,
              fontWeight: 700
            }}
            testID="timelogsIndexScreen/summary/entries/value"
          >
            {String(summaryCount)}
          </Text>
        </View>
        <View
          style={styles.summaryItemInvoiced ||= {
            gap: 4,
            minWidth: 160
          }}
          testID="timelogsIndexScreen/summary/invoiced"
        >
          <Text
            style={styles[`summaryLabelInvoiced-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              fontSize: 12,
              fontWeight: 600
            }}
            testID="timelogsIndexScreen/summary/invoiced/label"
          >
            {_("Invoiced / not invoiced")}
          </Text>
          <Text
            style={styles[`summaryValueInvoiced-${this.tt.themeName}`] ||= {
              fontSize: 20,
              fontWeight: 700
            }}
            testID="timelogsIndexScreen/summary/invoiced/value"
          >
            {`${summaryInvoicedCount} / ${summaryNotInvoicedCount}`}
          </Text>
        </View>
      </View>
    )
  }

  /**
   * Renders filter controls for the timelog list.
   * @returns {ReactNode} Filter controls.
   */
  renderFilters() {
    const {
      dateFrom,
      dateTo,
      invoicedFilter,
      projectIds,
      searchQuery,
      userIds
    } = this.s
    return (
      <View
        style={styles[`filtersCard-${this.tt.themeName}`] ||= {
          backgroundColor: this.tt.colors.surface,
          borderColor: this.tt.colors.border,
          borderRadius: 12,
          borderWidth: 1,
          gap: 12,
          marginBottom: 16,
          padding: 16
        }}
        testID="timelogsIndexScreen/filters"
      >
        <View
          style={styles.filterRow ||= {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 12
          }}
          testID="timelogsIndexScreen/filterDateRow"
        >
          <View
            style={styles.filterDateRangeField ||= {
              flex: 1,
              minWidth: 220
            }}
            testID="timelogsIndexScreen/dateRangeField"
          >
            <Text
              style={styles[`dateRangeLabel-${this.tt.themeName}`] ||= {
                color: this.tt.colors.textMuted,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.5,
                marginBottom: 6,
                textTransform: "uppercase"
              }}
              testID="timelogsIndexScreen/dateRangeLabel"
            >
              {_("Date range")}
            </Text>
            <DateRangePicker
              dateFrom={dateFrom}
              dateTo={dateTo}
              onChange={this.tt.onDateRangeChange}
              testID="timelogsIndexScreen/dateRangePicker"
            />
          </View>
          <View
            style={styles.searchField ||= {
              flex: 2,
              minWidth: 200
            }}
            testID="timelogsIndexScreen/searchField"
          >
            <Text
              style={styles[`searchLabel-${this.tt.themeName}`] ||= {
                color: this.tt.colors.textMuted,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.5,
                marginBottom: 6,
                textTransform: "uppercase"
              }}
              testID="timelogsIndexScreen/searchLabel"
            >
              {_("Search")}
            </Text>
            <TextInput
              defaultValue={searchQuery}
              onChangeText={this.tt.onSearchChangeText}
              placeholder={_("Search by comment...")}
              style={styles.searchInput ||= {borderRadius: 10}}
              testID="timelogsIndexScreen/searchInput"
            />
          </View>
        </View>
        <View
          style={styles.filterRow ||= {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 12
          }}
          testID="timelogsIndexScreen/filterMultiRow"
        >
          <View
            style={styles.userFilterField ||= {
              flex: 1,
              minWidth: 220
            }}
            testID="timelogsIndexScreen/userFilterField"
          >
            <Text
              style={styles[`userFilterLabel-${this.tt.themeName}`] ||= {
                color: this.tt.colors.textMuted,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.5,
                marginBottom: 6,
                textTransform: "uppercase"
              }}
              testID="timelogsIndexScreen/userFilterLabel"
            >
              {_("Users")}
            </Text>
            <UsersSelect
              closeOnChange
              multiple
              onChangeValue={this.tt.onUserFilterChange}
              placeholder={_("All users")}
              testID="timelogsIndexScreen/userFilter"
              values={userIds.map(String)}
            />
          </View>
          <View
            style={styles.projectFilterField ||= {
              flex: 1,
              minWidth: 220
            }}
            testID="timelogsIndexScreen/projectFilterField"
          >
            <Text
              style={styles[`projectFilterLabel-${this.tt.themeName}`] ||= {
                color: this.tt.colors.textMuted,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.5,
                marginBottom: 6,
                textTransform: "uppercase"
              }}
              testID="timelogsIndexScreen/projectFilterLabel"
            >
              {_("Projects")}
            </Text>
            <ProjectsSelect
              closeOnChange
              multiple
              onChangeValue={this.tt.onProjectFilterChange}
              placeholder={_("All projects")}
              testID="timelogsIndexScreen/projectFilter"
              values={projectIds.map(String)}
            />
          </View>
        </View>
        <View
          style={styles.filterRow ||= {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8
          }}
          testID="timelogsIndexScreen/invoicedFilterRow"
        >
          {renderInvoicedChip(this, _("Any"), "any", invoicedFilter)}
          {renderInvoicedChip(
            this,
            _("Not invoiced"),
            "not_invoiced",
            invoicedFilter
          )}
          {renderInvoicedChip(this, _("Invoiced"), "invoiced", invoicedFilter)}
        </View>
      </View>
    )
  }

  /**
   * Renders timelog rows, keeping previous rows visible during filter reloads.
   * @returns {ReactNode} Loading state, list, or empty state.
   */
  renderTimelogsResults() {
    if (this.s.loading && !this.s.loaded) {
      return <LoadingState testID="timelogsIndexScreen/loadingText" />
    }
    if (!this.s.loaded) return null
    return this.renderTimelogsList()
  }

  /**
   * Renders the timelog result list.
   * @returns {ReactNode} Result list or empty state.
   */
  renderTimelogsList() {
    const {timelogs} = this.s
    if (timelogs.length === 0) {
      return (
        <EmptyState
          message={_("No timelogs found")}
          testID="timelogsIndexScreen/emptyCard"
        />
      )
    }
    return (
      <View testID="timelogsIndexScreen/list">
        <View
          style={styles[`listHeader-${this.tt.themeName}`] ||= {
            borderBottomColor: this.tt.colors.border,
            borderBottomWidth: 1,
            flexDirection: "row",
            paddingBottom: 8
          }}
          testID="timelogsIndexScreen/listHeader"
        >
          <Text
            style={styles[`colDate-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              flex: 1,
              fontSize: 13,
              fontWeight: 700,
              textAlign: "left"
            }}
            testID="timelogsIndexScreen/listHeader/date"
          >
            {_("Date")}
          </Text>
          <Text
            style={styles[`colUser-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              flex: 1,
              fontSize: 13,
              fontWeight: 700,
              textAlign: "left"
            }}
            testID="timelogsIndexScreen/listHeader/user"
          >
            {_("User")}
          </Text>
          <Text
            style={styles[`colTask-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              flex: 2,
              fontSize: 13,
              fontWeight: 700,
              textAlign: "left"
            }}
            testID="timelogsIndexScreen/listHeader/task"
          >
            {_("Task")}
          </Text>
          <Text
            style={styles[`colTime-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              flex: 1,
              fontSize: 13,
              fontWeight: 700,
              textAlign: "right"
            }}
            testID="timelogsIndexScreen/listHeader/time"
          >
            {_("Time")}
          </Text>
          <Text
            style={styles[`colTransport-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              flex: 1,
              fontSize: 13,
              fontWeight: 700,
              textAlign: "right"
            }}
            testID="timelogsIndexScreen/listHeader/transport"
          >
            {_("Transport length")}
          </Text>
          <Text
            style={styles[`colComment-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              flex: 2,
              fontSize: 13,
              fontWeight: 700,
              textAlign: "left"
            }}
            testID="timelogsIndexScreen/listHeader/comment"
          >
            {_("Comment")}
          </Text>
          <Text
            style={styles[`colInvoiced-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              flex: 1,
              fontSize: 13,
              fontWeight: 700,
              textAlign: "left"
            }}
            testID="timelogsIndexScreen/listHeader/invoiced"
          >
            {_("Invoiced")}
          </Text>
        </View>
        {timelogs.map((timelog) => this.renderTimelogRow(timelog))}
      </View>
    )
  }

  /**
   * Renders one timelog row.
   * @param {Timelog} timelog Timelog to render.
   * @returns {ReactNode} Timelog row content.
   */
  renderTimelogRow(timelog) {
    const timelogId = String(timelog.id())
    const user = timelog.user()
    const task = timelog.task()
    const userName = user
      ? `${user.firstName() || ""} ${user.lastName() || ""}`.trim()
      : ""
    const invoiced = timelog.invoiced()
    const timelogHref = buildRoutePath(this.tt.params, `/timelogs/${timelogId}`)
    return (
      <View
        key={timelogId}
        style={styles[`listRow-${this.tt.themeName}`] ||= {
          borderBottomColor: this.tt.colors.divider,
          borderBottomWidth: 1,
          flexDirection: "row",
          paddingVertical: 10
        }}
        testID={`timelogsIndexScreen/timelog-${timelogId}`}
      >
        <Link
          href={timelogHref}
          style={styles[`cellDate-${this.tt.themeName}`] ||= {
            flex: 1
          }}
          testID={`timelogsIndexScreen/timelog-${timelogId}/date`}
        >
          {formatDate(timelog.date())}
        </Link>
        {user ?
          <Link
            href={buildRoutePath(this.tt.params, `/users/${user.id()}`)}
            style={styles[`cellUser-${this.tt.themeName}`] ||= {
              flex: 1
            }}
            testID={`timelogsIndexScreen/timelog-${timelogId}/user`}
          >
            {userName}
          </Link>
          :
          <Text
            style={styles[`cellUser-${this.tt.themeName}`]}
            testID={`timelogsIndexScreen/timelog-${timelogId}/user`}
          >
            {userName}
          </Text>
        }
        {task ?
          <Link
            href={buildRoutePath(
              this.tt.params,
              task.projectId()
                ? `/tasks/${task.id()}?projectId=${encodeURIComponent(task.projectId())}`
                : `/tasks/${task.id()}`
            )}
            style={styles[`cellTask-${this.tt.themeName}`] ||= {
              flex: 2
            }}
            testID={`timelogsIndexScreen/timelog-${timelogId}/task`}
          >
            {task.name() || ""}
          </Link>
          :
          <Text
            style={styles[`cellTask-${this.tt.themeName}`]}
            testID={`timelogsIndexScreen/timelog-${timelogId}/task`}
          >
            {""}
          </Text>
        }
        <Link
          href={timelogHref}
          style={styles[`cellTime-${this.tt.themeName}`] ||= {
            flex: 1,
            textAlign: "right"
          }}
          testID={`timelogsIndexScreen/timelog-${timelogId}/time`}
        >
          {formatTime(timelog.timeSeconds())}
        </Link>
        <Link
          href={timelogHref}
          style={styles[`cellTransport-${this.tt.themeName}`] ||= {
            flex: 1,
            textAlign: "right"
          }}
          testID={`timelogsIndexScreen/timelog-${timelogId}/transport`}
        >
          {timelog.transportLength() ? `${timelog.transportLength()} km` : ""}
        </Link>
        <Link
          href={timelogHref}
          style={styles[`cellComment-${this.tt.themeName}`] ||= {
            color: this.tt.colors.textMuted,
            flex: 2
          }}
          testID={`timelogsIndexScreen/timelog-${timelogId}/comment`}
        >
          {timelog.comment() || ""}
        </Link>
        <Link
          href={timelogHref}
          style={styles[`cellInvoiced-${this.tt.themeName}-${invoiced}`] ||= {
            color: invoiced ? this.tt.colors.successText : this.tt.colors.textMuted,
            flex: 1,
            fontSize: 13,
            fontWeight: 600
          }}
          testID={`timelogsIndexScreen/timelog-${timelogId}/invoiced`}
        >
          {invoiced ? _("Yes") : _("No")}
        </Link>
      </View>
    )
  }

  /**
   * Commits a picked date range into state and the route in one navigation.
   * @param {{dateFrom: string, dateTo: string}} range Selected range as ISO date strings.
   * @returns {void} Nothing.
   */
  onDateRangeChange = ({dateFrom, dateTo}) => {
    if (!isValidDateFilterValue(dateFrom) || !isValidDateFilterValue(dateTo)) return
    if (dateFrom === this.s.dateFrom && dateTo === this.s.dateTo) return
    this.tt.pushFilterQueryParams({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined
    })
    this.s.dateFrom = dateFrom
    this.s.dateTo = dateTo
    this.s.page = 1
  }

  /**
   * Debounces search filter updates into the route.
   * @param {string} value Search text entered by the user.
   * @returns {void} Nothing.
   */
  onSearchChangeText = (value) => {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
    this.searchDebounceTimer = setTimeout(() => {
      this.tt.pushFilterQueryParams({
        search: value || undefined
      })
      this.s.page = 1
      this.s.searchQuery = value
    }, 300)
  }

  /**
   * Applies selected user filters.
   * @param {(string | number)[] | undefined} values Selected user values.
   * @returns {void} Nothing.
   */
  onUserFilterChange = (values) => {
    const nextIds = identifierListParam(values, "User IDs")
    if (sameIds(nextIds, this.s.userIds)) return
    this.tt.pushFilterQueryParams({
      user_ids: nextIds.length === 0 ? undefined : nextIds.map(String)
    })
    this.s.page = 1
    this.s.userIds = nextIds
  }

  /**
   * Applies selected project filters.
   * @param {(string | number)[] | undefined} values Selected project values.
   * @returns {void} Nothing.
   */
  onProjectFilterChange = (values) => {
    const nextIds = identifierListParam(values, "Project IDs")
    if (sameIds(nextIds, this.s.projectIds)) return
    this.tt.pushFilterQueryParams({
      project_ids: nextIds.length === 0 ? undefined : nextIds.map(String)
    })
    this.s.page = 1
    this.s.projectIds = nextIds
  }

  /**
   * Applies the invoiced-state filter.
   * @param {InvoicedFilter} filter Invoiced-state filter to apply.
   * @returns {void} Nothing.
   */
  onInvoicedFilterPress = (filter) => {
    if (filter === this.s.invoicedFilter) return
    this.tt.pushFilterQueryParams({
      invoiced: filter === "any" ? undefined : filter
    })
    this.s.invoicedFilter = filter
    this.s.page = 1
  }

  /**
   * Updates the active result page.
   * @param {number} nextPage Page selected by pagination.
   * @returns {void} Nothing.
   */
  onPageChange = (nextPage) => {
    if (nextPage === this.s.page) return
    this.s.page = nextPage
  }

  /**
   * Pushes filter query params while carrying visible rows through the stack update.
   * @param {Record<string, string | string[] | undefined>} updates Query params to merge into the current URL.
   * @returns {void} Nothing.
   */
  pushFilterQueryParams(updates) {
    const nextPath = pathForQueryParamUpdates(this.tt.currentQueryParams(), "/timelogs", updates)
    if (!nextPath) return
    this.preparePendingFilterTransitionState(nextPath)
    this.tt.router.push(nextPath)
  }

  /**
   * Keeps current rows visible for the immediate filter navigation only.
   * @param {string} nextPath Route path that will be pushed.
   * @returns {void} Nothing.
   */
  preparePendingFilterTransitionState(nextPath) {
    pendingFilterTransitionState = {
      routeFiltersKey: serializeFilters(filtersFromParams(queryParamsFromPath(nextPath))),
      sourceFiltersKey: this.tt.routeFiltersKey,
      summaryCount: this.s.summaryCount,
      summaryInvoicedCount: this.s.summaryInvoicedCount,
      summaryLoaded: this.s.summaryLoaded,
      summaryNotInvoicedCount: this.s.summaryNotInvoicedCount,
      summaryTotalSeconds: this.s.summaryTotalSeconds,
      summaryTotalTransportSeconds: this.s.summaryTotalTransportSeconds,
      timelogs: this.s.timelogs,
      totalCount: this.s.totalCount
    }
  }

  /**
   * Consumes rows carried across a same-route filter transition.
   * @param {string} routeFiltersKey Active filter key for the mounted route.
   * @returns {void} Nothing.
   */
  consumePendingFilterTransitionState(routeFiltersKey) {
    const pendingState = pendingFilterTransitionState
    if (!pendingState) return

    if (pendingState.sourceFiltersKey === routeFiltersKey) {
      this.applyPendingFilterTransitionState(pendingState)
      return
    }

    pendingFilterTransitionState = null
    if (pendingState.routeFiltersKey !== routeFiltersKey) return

    this.applyPendingFilterTransitionState(pendingState)
  }

  /**
   * Applies pending rows to this screen instance.
   * @param {PendingTimelogsIndexFilterTransition} pendingState Rows and summary to render during filter refresh.
   * @returns {void} Nothing.
   */
  applyPendingFilterTransitionState(pendingState) {
    this.state.loaded = true
    this.state.loading = false
    this.state.summaryCount = pendingState.summaryCount
    this.state.summaryInvoicedCount = pendingState.summaryInvoicedCount
    this.state.summaryLoaded = pendingState.summaryLoaded
    this.state.summaryNotInvoicedCount = pendingState.summaryNotInvoicedCount
    this.state.summaryTotalSeconds = pendingState.summaryTotalSeconds
    this.state.summaryTotalTransportSeconds = pendingState.summaryTotalTransportSeconds
    this.state.timelogs = pendingState.timelogs
    this.state.totalCount = pendingState.totalCount
  }

  /**
   * Marks all matching timelogs as invoiced after confirmation.
   * @returns {Promise<void>} Resolves after bulk marking finishes.
   * @rejects {Error} When the bulk update fails.
   */
  onMarkInvoicedPress = async () => {
    const {
      dateFrom,
      dateTo,
      invoicedFilter,
      projectIds,
      searchQuery,
      userIds
    } = this.s
    const ok = await confirmModal(
      _("Mark all matching timelogs as invoiced?"),
      {
        cancelLabel: _("Cancel"),
        confirmLabel: _("Mark all invoiced"),
        title: _("Confirm")
      }
    )
    if (!ok) return
    this.s.marking = true
    this.s.status = null
    const startedAt = performanceNow()
    try {
      const response = await Timelog.markMatchingInvoiced({
        commentCont: searchQuery,
        dateFrom,
        dateTo,
        invoicedFilter,
        projectIds: projectIds.map(String),
        userIds: userIds.map(String)
      })
      const markedCount = response.markedCount
      performanceMeasure("timelogsIndex.markMatchingInvoiced", startedAt, {
        markedCount
      })
      FlashNotifications.success(
        _("Marked %{count} timelogs as invoiced", {
          count: String(markedCount)
        })
      )
      this.s.marking = false
      this.s.page = 1
      void this.loadTimelogs()
    } catch (error) {
      FlashNotifications.errorResponse(error)
      this.s.marking = false
      throw error
    }
  }

  /**
   * Exports matching timelogs to CSV.
   * @returns {Promise<void>} Resolves after export handling finishes.
   * @rejects {Error} When CSV export fails.
   */
  onExportCsvPress = async () => {
    const {
      dateFrom,
      dateTo,
      invoicedFilter,
      projectIds,
      searchQuery,
      userIds
    } = this.s
    this.s.exporting = true
    this.s.status = null
    try {
      const response = await Timelog.csvExport({
        commentCont: searchQuery,
        dateFrom,
        dateTo,
        invoicedFilter,
        projectIds: projectIds.map(String),
        userIds: userIds.map(String)
      })
      const csv = response.csv
      const filename = response.filename
      const count = response.count
      triggerCsvDownload(csv, filename)
      FlashNotifications.success(
        _("Exported %{count} timelogs", {
          count: String(count)
        })
      )
      this.s.exporting = false
    } catch (error) {
      FlashNotifications.errorResponse(error)
      this.s.exporting = false
      throw error
    }
  }

  /**
   * Loads aggregate summary data for the current filters.
   * @returns {Promise<void>} Resolves after summary handling finishes.
   */
  loadSummary = async () => {
    const {
      dateFrom,
      dateTo,
      invoicedFilter,
      projectIds,
      searchQuery,
      userIds
    } = this.s
    try {
      const startedAt = performanceNow()
      const summary = await Timelog.timelogsSummary({
        commentCont: searchQuery,
        dateFrom,
        dateTo,
        invoicedFilter,
        projectIds: projectIds.map(String),
        userIds: userIds.map(String)
      })
      this.s.summaryCount = summary.count
      this.s.summaryInvoicedCount = summary.invoicedCount
      this.s.summaryLoaded = true
      this.s.summaryNotInvoicedCount = summary.notInvoicedCount
      this.s.summaryTotalSeconds = summary.totalSeconds
      this.s.summaryTotalTransportSeconds = summary.totalTransportSeconds
      performanceMeasure("timelogsIndex.loadSummary", startedAt, {
        count: summary.count
      })
    } catch (error) {
      // Summary is decorative — surface failures via console only so the
      // main list stays usable even if the aggregate query errors out.
      if (typeof console !== "undefined")
        console.warn("timelogs summary failed", error)
    }
  }

  /**
   * Loads the current page of timelogs.
   * @returns {Promise<void>} Resolves after row loading finishes.
   * @rejects {Error} When row loading fails.
   */
  loadTimelogs = async () => {
    const {
      dateFrom,
      dateTo,
      invoicedFilter,
      page,
      projectIds,
      searchQuery,
      userIds
    } = this.s
    const requestId = ++this.loadRequestId
    this.s.loading = !this.s.loaded
    this.s.status = null
    const startedAt = performanceNow()
    try {
      const ransackParams = buildRansackParams({
        dateFrom,
        dateTo,
        invoicedFilter,
        projectIds,
        searchQuery,
        userIds
      })
      const baseQuery = Timelog.ransack(ransackParams)
      const [timelogs, totalCount] = await Promise.all([
        baseQuery
          .clone()
          .preload({
            task: true,
            user: true
          })
          .select(TIMELOG_INDEX_SELECT)
          .select(TIMELOG_INDEX_PRELOAD_SELECT)
          .page(page)
          .perPage(PAGE_SIZE)
          .toArray(),
        baseQuery.clone().count()
      ])
      if (this.loadRequestId !== requestId) return
      this.s.loaded = true
      this.s.timelogs = timelogs
      this.s.loading = false
      this.s.totalCount = totalCount
      performanceMeasure("timelogsIndex.loadTimelogs", startedAt, {
        page,
        rowCount: timelogs.length,
        totalCount
      })
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.loaded = true
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }
}

/** @type {NamedExoticComponent<TimelogsIndexScreenProps>} */
const TimelogsIndexScreenComponent = memo(shapeComponent(TimelogsIndexScreen))

export default TimelogsIndexScreenComponent

/**
 * Renders one invoiced-state filter chip.
 * @param {TimelogsIndexScreen} screen Screen instance handling chip presses.
 * @param {string} label Visible chip label.
 * @param {InvoicedFilter} filter Filter value represented by the chip.
 * @param {InvoicedFilter} active Current active filter value.
 * @returns {ReactNode} Pressable invoiced filter chip.
 */
function renderInvoicedChip(screen, label, filter, active) {
  const isActive = filter === active
  const themeName = screen.tt.themeName
  const colors = screen.tt.colors
  return (
    <Pressable
      key={filter}
      onPress={() => screen.onInvoicedFilterPress(filter)}
      style={styles[`invoicedChip-${themeName}-${filter}-${isActive}`] ||= {
        alignItems: "center",
        backgroundColor: isActive ? colors.primary : colors.surface,
        borderColor: isActive ? colors.primary : colors.border,
        borderRadius: 999,
        borderWidth: 1,
        flexDirection: "row",
        paddingHorizontal: 14,
        paddingVertical: 8
      }}
      testID={`timelogsIndexScreen/invoicedChip/${filter}`}
    >
      <View
        style={styles.invoicedChipIcon ||= {
          marginRight: 8,
          width: 14
        }}
        testID={`timelogsIndexScreen/invoicedChip/${filter}/icon`}
      >
        <FontAwesome6 color={isActive ? colors.onPrimary : colors.textMuted} name="file-invoice" size={13} />
      </View>
      <Text
        style={styles[`invoicedChipText-${themeName}-${filter}-${isActive}`] ||= {
          color: isActive ? colors.onPrimary : colors.textMuted,
          fontSize: 13,
          fontWeight: 700
        }}
        testID={`timelogsIndexScreen/invoicedChip/${filter}/text`}
      >
        {label}
      </Text>
    </Pressable>
  )
}

/**
 * Builds timelog list filters from route params.
 * @param {RouteQueryParams} params Route params to parse.
 * @returns {TimelogsIndexFilters} Parsed filter values.
 */
function filtersFromParams(params) {
  return {
    dateFrom: optionalRouteParam(params, "date_from", "Date from") ?? firstOfMonth(),
    dateTo: optionalRouteParam(params, "date_to", "Date to") ?? today(),
    invoicedFilter: parseInvoicedFilter(params.invoiced),
    projectIds: identifierListParam(params.project_ids, "Project IDs"),
    searchQuery: optionalRouteParam(params, "search", "Search") ?? "",
    userIds: identifierListParam(params.user_ids, "User IDs")
  }
}

/**
 * Serializes filters into a stable comparison key.
 * @param {TimelogsIndexFilters} filters Filters to serialize.
 * @returns {string} Stable serialized key.
 */
function serializeFilters(filters) {
  return JSON.stringify({
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    invoicedFilter: filters.invoicedFilter,
    projectIds: filters.projectIds,
    searchQuery: filters.searchQuery,
    userIds: filters.userIds
  })
}

/**
 * Parses a route path into a query-param object.
 * @param {string} path Route path with optional search params.
 * @returns {RouteQueryParams} Parsed params preserving repeated values as arrays.
 */
function queryParamsFromPath(path) {
  const url = new URL(path, "http://placeholder")
  /** @type {RouteQueryParams} */
  const params = {}
  for (const [key, value] of url.searchParams.entries()) {
    const existingValue = params[key]
    if (Array.isArray(existingValue)) {
      existingValue.push(value)
    } else if (existingValue === undefined) {
      params[key] = value
    } else {
      params[key] = [existingValue, value]
    }
  }
  return params
}

/**
 * Checks whether a date filter value is empty or a real ISO date.
 * @param {string} value Candidate date string from the input.
 * @returns {boolean} True when the value can be committed.
 */
function isValidDateFilterValue(value) {
  if (value === "") return true
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const [year, month, day] = value.split("-").map((part) => Number.parseInt(part, 10))
  const date = new Date(Date.UTC(year, month - 1, day))
  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
}

/**
 * Compares two sorted identifier lists.
 * @param {string[]} a First identifier list.
 * @param {string[]} b Second identifier list.
 * @returns {boolean} True when both lists contain the same ids in order.
 */
function sameIds(a, b) {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

/**
 * Parses a route param into an invoiced-state filter.
 * @param {unknown} value Route parameter value.
 * @returns {InvoicedFilter} Parsed invoiced-state filter.
 */
function parseInvoicedFilter(value) {
  if (value === "invoiced" || value === "not_invoiced") return value
  return "any"
}

/**
 * Builds ransack parameters for timelog list queries.
 * @param {{dateFrom: string, dateTo: string, invoicedFilter: InvoicedFilter, projectIds: string[], searchQuery: string, userIds: string[]}} args Filter state values.
 * @returns {Record<string, unknown>} Ransack query parameters.
 */
function buildRansackParams({
  dateFrom,
  dateTo,
  invoicedFilter,
  projectIds,
  searchQuery,
  userIds
}) {
  /** @type {Record<string, unknown>} */
  const ransackParams = {
    s: "date desc, id desc"
  }
  if (dateFrom) ransackParams.dateGteq = dateFrom
  if (dateTo) ransackParams.dateLteq = dateTo
  if (searchQuery) ransackParams.commentCont = searchQuery
  if (userIds.length > 0) ransackParams.userIdIn = userIds.map(String)
  if (projectIds.length > 0) ransackParams.taskProjectIdIn = projectIds.map(String)
  if (invoicedFilter === "invoiced") ransackParams.invoicedEq = true
  if (invoicedFilter === "not_invoiced") ransackParams.invoicedEq = false
  return ransackParams
}

/**
 * Starts a browser CSV download when the DOM APIs are available.
 * @param {string} csv CSV content.
 * @param {string} filename Name assigned to the downloaded file.
 * @returns {void} Nothing.
 */
function triggerCsvDownload(csv, filename) {
  if (typeof document === "undefined" || typeof Blob === "undefined") return
  const blob = new Blob([csv], {
    type: "text/csv;charset=utf-8;"
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
